import type { CSSProperties, FC, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import useEmblaCarousel from 'embla-carousel-react';

import { cn } from '../utils/cn';
import { rewriteImageSrc } from '../utils/rewriteImageSrc';

import { useImagePriority } from '../dreamComponentProvider/hooks';
import { Link } from '../link';
import { PictureElement } from '../pictureElement/Picture';
import type { CarouselVariant, GalleryColumns, GalleryLayout, SecondaryImage, ThumbnailPosition } from './Image.types';
import * as styles from './ImageCarousel.css';

type ImageCarouselProps = {
  inertImage?: boolean;
  primaryImage: { src: string; alt?: string; href?: string };
  secondaryImages: SecondaryImage[];
  hideLink?: boolean;
  internal_link?: boolean;
  open_in_new_tab?: boolean;
  variant?: CarouselVariant;
  showArrows?: boolean;
  arrowColor?: string;
  arrowIconColor?: string;
  dotColor?: string;
  dotActiveColor?: string;
  counterBackgroundColor?: string;
  counterTextColor?: string;
  imageStyles?: CSSProperties;
  layout?: GalleryLayout;
  aspectRatio?: number;
  columns?: GalleryColumns;
  imageWidth?: number;
  mobileImageWidth?: number;
  isFullWidth?: boolean;
  mobileIsFullWidth?: boolean;
  firstImageFullWidth?: boolean;
  gap?: number;
  thumbnailPosition?: ThumbnailPosition;
  thumbnailHeight?: number;
  thumbnailBorderRadius?: string;
  mobileThumbnailBorderRadius?: string;
  mobileLayout?: GalleryLayout;
  mobileColumns?: GalleryColumns;
  mobileThumbnailPosition?: ThumbnailPosition;
  mobileGap?: number;
  mobileVariant?: CarouselVariant;
  mobilePadding?: string;
};

export const ImageCarousel: FC<ImageCarouselProps> = ({
  inertImage,
  primaryImage,
  secondaryImages,
  hideLink,
  internal_link,
  open_in_new_tab,
  variant = 'arrows-dots-bottom',
  showArrows = true,
  arrowColor,
  arrowIconColor,
  dotColor,
  dotActiveColor,
  counterBackgroundColor,
  counterTextColor,
  imageStyles,
  layout = 'carousel',
  aspectRatio,
  columns = '2',
  imageWidth,
  firstImageFullWidth = false,
  gap,
  thumbnailPosition = 'bottom',
  thumbnailHeight: thumbnailHeightProp,
  thumbnailBorderRadius,
  mobileThumbnailBorderRadius,
  mobileLayout,
  mobileColumns,
  mobileThumbnailPosition,
  mobileGap,
  mobileVariant,
  mobilePadding,
  mobileImageWidth,
}) => {
  const emblaOptions = useMemo(
    () => ({
      loop: true,
      watchDrag: variant !== 'dots-bottom',
    }),
    [variant],
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [primaryImageAspectRatio, setPrimaryImageAspectRatio] = useState<number | undefined>(undefined);
  const { fetchPriority, loading } = useImagePriority();

  const allImages = [{ url: primaryImage.src, alt: primaryImage.alt, href: primaryImage.href }, ...secondaryImages];

  const totalSlides = allImages.length;

  // Use explicit aspectRatio if provided, otherwise use the primary image's natural aspect ratio
  const effectiveAspectRatio = aspectRatio ?? primaryImageAspectRatio;

  const handlePrimaryImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (!aspectRatio) {
        const img = e.currentTarget;
        const naturalAspectRatio = img.naturalWidth / img.naturalHeight;
        setPrimaryImageAspectRatio(naturalAspectRatio);
      }
    },
    [aspectRatio],
  );

  const updateSelectedIndex = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    updateSelectedIndex();
    emblaApi.on('select', updateSelectedIndex);
    emblaApi.on('reInit', updateSelectedIndex);
    return () => {
      emblaApi.off('select', updateSelectedIndex);
      emblaApi.off('reInit', updateSelectedIndex);
    };
  }, [emblaApi, updateSelectedIndex]);

  const scrollPrev = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollPrev();
    },
    [emblaApi],
  );

  const scrollNext = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollNext();
    },
    [emblaApi],
  );

  const scrollTo = useCallback(
    (index: number, e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const renderDotsControls = (hideClass?: string) => (
    <div className={cn(styles.carouselControls, hideClass)}>
      {allImages.map((image, index) => (
        <button
          key={`dot-${image.url}-${index}`}
          type="button"
          className={cn(styles.carouselDot, index === selectedIndex && styles.carouselDotActive)}
          onClick={(e) => scrollTo(index, e)}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );

  const renderNumbersControls = (hideClass?: string) => (
    <div className={cn(styles.carouselControls, hideClass)}>
      <div className={styles.carouselNumbersContainer}>
        <span className={styles.carouselNumbers}>
          {selectedIndex + 1}/{totalSlides}
        </span>
      </div>
    </div>
  );

  const renderOverlayControls = () => {
    const effectiveMobileVariant = mobileVariant ?? variant;
    const needsBothVariants = mobileVariant && mobileVariant !== variant;

    if (needsBothVariants) {
      return (
        <>
          {variant === 'arrows-dots-bottom' && renderDotsControls(styles.hideOnMobile)}
          {variant === 'arrows-numbers-bottom' && renderNumbersControls(styles.hideOnMobile)}
          {variant === 'dots-bottom' && renderDotsControls(styles.hideOnMobile)}
          {effectiveMobileVariant === 'arrows-dots-bottom' && renderDotsControls(styles.hideOnDesktop)}
          {effectiveMobileVariant === 'arrows-numbers-bottom' && renderNumbersControls(styles.hideOnDesktop)}
          {effectiveMobileVariant === 'dots-bottom' && renderDotsControls(styles.hideOnDesktop)}
        </>
      );
    }

    switch (variant) {
      case 'arrows-dots-bottom':
      case 'dots-bottom':
        return renderDotsControls();
      case 'arrows-numbers-bottom':
        return renderNumbersControls();
      default:
        return null;
    }
  };

  const getMobileThumbnailClass = () => {
    const pos = mobileThumbnailPosition;
    if (!pos) return undefined;
    const isVertical = pos === 'left' || pos === 'right';
    return isVertical ? styles.carouselThumbnailsMobileVertical : styles.carouselThumbnailsMobileHorizontal;
  };

  const renderThumbnails = () => {
    const effectiveMobileVariant = mobileVariant ?? variant;
    const showOnDesktop = variant === 'thumbnails-bottom';
    const showOnMobile = effectiveMobileVariant === 'thumbnails-bottom';

    if (!showOnDesktop && !showOnMobile) return null;

    const isVertical = thumbnailPosition === 'left' || thumbnailPosition === 'right';

    // For vertical layouts (left/right), we need to account for the space thumbnails take
    // Solving: imageHeight = (containerWidth - gap) / (aspectRatio * 1.16)
    // where 1.16 = 1 + 0.16 (thumbnail is 16% of image height)
    let computedImageHeight: number | undefined;
    let computedMobileImageHeight: number | undefined;
    if (imageWidth && effectiveAspectRatio) {
      if (isVertical) {
        const rawGap = gap ?? Math.max(8, (imageWidth / effectiveAspectRatio) * 0.03);
        const gapValue = Math.min(rawGap, imageWidth * 0.3);
        computedImageHeight = (imageWidth - gapValue) / (effectiveAspectRatio * 1.16);

        if (mobileImageWidth !== undefined) {
          const mobileWidth = mobileImageWidth ?? imageWidth;
          const rawMobileGap = mobileGap ?? rawGap;
          const mobileGapValue = Math.min(rawMobileGap, mobileWidth * 0.3);
          computedMobileImageHeight = (mobileWidth - mobileGapValue) / (effectiveAspectRatio * 1.16);
        }
      } else {
        computedImageHeight = imageWidth / effectiveAspectRatio;
      }
    }

    const thumbnailHeight =
      thumbnailHeightProp ?? (computedImageHeight ? Math.max(computedImageHeight * 0.16, 32) : 80);
    const mobileThumbnailHeight = Math.min(thumbnailHeight * 0.6, 56);

    const getVisibilityClass = () => {
      if (showOnDesktop && !showOnMobile) return styles.hideOnMobile;
      if (!showOnDesktop && showOnMobile) return styles.hideOnDesktopThumbnails;
      return undefined;
    };

    return (
      <div
        className={cn(
          styles.carouselThumbnails,
          isVertical && styles.carouselThumbnailsVertical,
          getMobileThumbnailClass(),
          getVisibilityClass(),
        )}
        style={
          {
            '--thumbnail-aspect-ratio': effectiveAspectRatio ?? 1.9,
            '--thumbnail-height': `${thumbnailHeight}px`,
            '--thumbnail-height-mobile': `${mobileThumbnailHeight}px`,
            '--thumbnail-border-radius': thumbnailBorderRadius || undefined,
            '--thumbnail-border-radius-mobile': mobileThumbnailBorderRadius || undefined,
            '--thumbnail-gap': scaledGap,
            '--thumbnail-gap-mobile': scaledMobileGap,
            '--thumbnail-container-height': computedImageHeight ? `${computedImageHeight}px` : '400px',
            '--thumbnail-container-height-mobile': computedMobileImageHeight
              ? `${computedMobileImageHeight}px`
              : undefined,
          } as CSSProperties
        }
      >
        {allImages.map((image, index) => (
          <button
            key={`thumb-${image.url}-${index}`}
            type="button"
            className={cn(styles.carouselThumbnail, index === selectedIndex && styles.carouselThumbnailActive)}
            onClick={(e) => scrollTo(index, e)}
            aria-label={`Go to slide ${index + 1}`}
          >
            <img
              src={rewriteImageSrc(image.url)}
              alt={image.alt || `Thumbnail ${index + 1}`}
              className={styles.carouselThumbnailImg}
              {...(inertImage ? { inert: true } : {})}
            />
          </button>
        ))}
      </div>
    );
  };

  if (totalSlides <= 1) {
    return null;
  }

  const getSlideStyle = (): CSSProperties => ({
    aspectRatio: effectiveAspectRatio,
  });

  const getImageStyle = (_isPrimary: boolean): CSSProperties => {
    // All main carousel images use contain to fit inside the container
    // while maintaining their original aspect ratio.
    // The container uses the primary image's aspect ratio.
    // Thumbnails (rendered separately) use cover to fill.
    return {
      ...imageStyles,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    };
  };

  const imageHeight = imageWidth && effectiveAspectRatio ? imageWidth / effectiveAspectRatio : undefined;
  const defaultGap = imageHeight ? Math.max(8, imageHeight * 0.03) : 16;
  const defaultMobileGap = Math.max(4, Math.round(defaultGap * 0.5));
  const scaledGap = `${gap ?? defaultGap}px`;
  const scaledMobileGap = `${mobileGap ?? defaultMobileGap}px`;

  const getMobileColumnsClass = () => {
    if (!mobileColumns) return undefined;
    return mobileColumns === '1' ? styles.gridMobileOneColumn : styles.gridMobileTwoColumns;
  };

  const wrapInLink = (content: React.ReactNode, href?: string) => {
    if (!href || hideLink) return content;
    return (
      <Link href={href} internal_link={internal_link} open_in_new_tab={open_in_new_tab} className={styles.imageLink}>
        {content}
      </Link>
    );
  };

  const renderGrid = (hideClass?: string) => (
    <div
      className={cn(
        styles.gridContainer,
        columns === '1' ? styles.gridOneColumn : styles.gridTwoColumns,
        getMobileColumnsClass(),
        hideClass,
      )}
      style={
        {
          '--carousel-arrow-color': arrowColor || undefined,
          '--grid-gap': scaledGap,
          '--grid-gap-mobile': scaledMobileGap,
          '--carousel-padding-mobile': mobilePadding || undefined,
        } as CSSProperties
      }
    >
      {allImages.map((image, index) => (
        <div
          key={`grid-${image.url}-${index}`}
          className={cn(
            styles.gridItem,
            index === 0 && firstImageFullWidth && columns === '2' && styles.gridItemFullWidth,
          )}
          style={getSlideStyle()}
        >
          {wrapInLink(
            <PictureElement src={rewriteImageSrc(image.url)}>
              <img
                src={rewriteImageSrc(image.url)}
                alt={image.alt || `Image ${index + 1}`}
                className={styles.gridImage}
                style={getImageStyle(index === 0)}
                onLoad={index === 0 ? handlePrimaryImageLoad : undefined}
                fetchPriority={index === 0 ? fetchPriority : undefined}
                loading={index === 0 ? loading : 'lazy'}
                {...(inertImage ? { inert: true } : {})}
              />
            </PictureElement>,
            image.href,
          )}
        </div>
      ))}
    </div>
  );

  if (layout === 'grid' && mobileLayout !== 'carousel') {
    return renderGrid();
  }

  const getContainerPositionClass = () => {
    switch (thumbnailPosition) {
      case 'top':
        return styles.carouselContainerColumnReverse;
      case 'left':
        return styles.carouselContainerRowReverse;
      case 'right':
        return styles.carouselContainerRow;
      default:
        return undefined;
    }
  };

  const getMobileContainerPositionClass = () => {
    const pos = mobileThumbnailPosition;
    if (!pos) return undefined;
    switch (pos) {
      case 'top':
        return styles.carouselContainerMobileColumnReverse;
      case 'left':
        return styles.carouselContainerMobileRowReverse;
      case 'right':
        return styles.carouselContainerMobileRow;
      case 'bottom':
        return styles.carouselContainerMobileColumn;
      default:
        return undefined;
    }
  };

  const renderCarousel = (hideClass?: string) => (
    <div
      className={cn(
        styles.carouselContainer,
        getContainerPositionClass(),
        getMobileContainerPositionClass(),
        hideClass,
      )}
      style={
        {
          '--carousel-arrow-color': arrowColor || undefined,
          '--carousel-arrow-icon-color': arrowIconColor || undefined,
          '--carousel-gap': scaledGap,
          '--carousel-gap-mobile': scaledMobileGap,
          '--carousel-counter-bg': counterBackgroundColor || undefined,
          '--carousel-counter-text': counterTextColor || undefined,
          '--carousel-dot-color': dotColor || undefined,
          '--carousel-dot-active-color': dotActiveColor || undefined,
          '--carousel-padding-mobile': mobilePadding || undefined,
        } as CSSProperties
      }
    >
      <div className={styles.carouselViewportWrapper}>
        <div className={styles.carouselViewport} ref={emblaRef}>
          <div className={styles.carouselSlidesContainer}>
            {allImages.map((image, index) => (
              <div key={`slide-${image.url}-${index}`} className={styles.carouselSlide} style={getSlideStyle()}>
                {wrapInLink(
                  <PictureElement src={rewriteImageSrc(image.url)}>
                    <img
                      src={rewriteImageSrc(image.url)}
                      alt={image.alt || `Slide ${index + 1}`}
                      style={getImageStyle(index === 0)}
                      onLoad={index === 0 ? handlePrimaryImageLoad : undefined}
                      fetchPriority={index === 0 ? fetchPriority : undefined}
                      loading={index === 0 ? loading : 'lazy'}
                      {...(inertImage ? { inert: true } : {})}
                    />
                  </PictureElement>,
                  image.href,
                )}
              </div>
            ))}
          </div>
        </div>

        {(showArrows || variant === 'arrows-numbers-bottom') && variant !== 'dots-bottom' && (
          <>
            <button
              type="button"
              className={cn(styles.carouselArrow, styles.carouselArrowPrev)}
              onClick={scrollPrev}
              aria-label="Previous slide"
            >
              <CaretLeft size={20} weight="bold" />
            </button>

            <button
              type="button"
              className={cn(styles.carouselArrow, styles.carouselArrowNext)}
              onClick={scrollNext}
              aria-label="Next slide"
            >
              <CaretRight size={20} weight="bold" />
            </button>
          </>
        )}

        {renderOverlayControls()}
      </div>

      {renderThumbnails()}
    </div>
  );

  // Handle layout switching between desktop and mobile
  if (layout === 'grid' && mobileLayout === 'carousel') {
    return (
      <>
        {renderGrid(styles.hideOnMobile)}
        {renderCarousel(styles.hideOnDesktop)}
      </>
    );
  }

  if (layout === 'carousel' && mobileLayout === 'grid') {
    return (
      <>
        {renderCarousel(styles.hideOnMobile)}
        {renderGrid(styles.hideOnDesktopGrid)}
      </>
    );
  }

  return renderCarousel();
};
